<template>
  <div>
    <h2 class="mb-4">Standardization of an Acid and Base - Part I</h2>

    <p class="mb-2">
      a) Fill in the data table below using the data you collected during the titration of the
      sample of potassium acid phthalate, KHP.
    </p>

    <p class="pl-n8 mb-5">
      <v-simple-table dense>
        <thead>
          <tr>
            <td></td>
            <td style="text-align: center; font-weight: bold">Trial 1</td>
            <td style="text-align: center; font-weight: bold">Trial 2</td>
          </tr>

          <tr v-for="result in labResults1" :key="result.value">
            <td>
              <stemble-latex :content="result.property" />
            </td>
            <td class="centered-input py-2 px-2 mx-0">
              <calculation-input v-model="inputs[result.value1]" />
            </td>
            <td class="centered-input py-2 px-2 mx-0">
              <calculation-input v-model="inputs[result.value2]" />
            </td>
          </tr>
        </thead>
      </v-simple-table>
    </p>

    <p class="mb-2 mt-4">
      b) What was the average molarity of NaOH determined from the two trials that you conducted?
    </p>

    <calculation-input
      v-model="inputs.concNaOHAvg"
      class="mb-5"
      prepend-text="$\text{[NaOH]}_\text{avg}:$"
      append-text="$\text{M}$"
      :disabled="!allowEditing"
    />

    <h2 class="mb-4">Standardization of an Acid and Base - Part II</h2>

    <p class="mb-2">
      c) Fill in the data table below using the data you collected during the titration of the
      unknown solution of HCl.
    </p>

    <p class="pl-n8 mb-5">
      <v-simple-table dense>
        <thead>
          <tr>
            <td></td>
            <td style="text-align: center; font-weight: bold">Trial 1</td>
            <td style="text-align: center; font-weight: bold">Trial 2</td>
          </tr>

          <tr v-for="result in labResults2A" :key="result.value">
            <td>
              <stemble-latex :content="result.property" />
            </td>
            <td class="centered-input py-2 px-2 mx-0">
              <calculation-input v-model="inputs[result.value1]" />
            </td>
            <td class="centered-input py-2 px-2 mx-0">
              <calculation-input v-model="inputs[result.value2]" />
            </td>
          </tr>

          <tr>
            <td>7. Volume of HCl (mL)</td>
            <td class="centered-input py-2 px-2 mx-0">
              <calculation-input placeholder="20.00" :disabled="true" />
            </td>
            <td class="centered-input py-2 px-2 mx-0">
              <calculation-input placeholder="20.00" :disabled="true" />
            </td>
          </tr>

          <tr v-for="result in labResults2B" :key="result.value">
            <td>
              <stemble-latex :content="result.property" />
            </td>
            <td class="centered-input py-2 px-2 mx-0">
              <calculation-input v-model="inputs[result.value1]" />
            </td>
            <td class="centered-input py-2 px-2 mx-0">
              <calculation-input v-model="inputs[result.value2]" />
            </td>
          </tr>
          <tr>
            <td colspan="3">
              <stemble-latex
                content="$^\text{*}\text{\small{These values are auto-populated from your answer to Part b)}}$"
              />
            </td>
          </tr>
        </thead>
      </v-simple-table>
    </p>

    <p class="mb-2 mt-4">
      d) What was the average molarity of HCl determined from the two trials that you performed?
    </p>

    <calculation-input
      v-model="inputs.concHClAvg"
      class="mb-5"
      prepend-text="$\text{[HCl]}_\text{avg}:$"
      append-text="$\text{M}$"
      :disabled="!allowEditing"
    />
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import StembleLatex from '@/tasks/components/StembleLatex';
import CalculationInput from '@/tasks/components/inputs/CalculationInput.vue';

export default {
  name: 'Ubc2025TitrationReportSheet1',
  components: {
    CalculationInput,
    StembleLatex,
  },
  mixins: [DynamicQuestionMixin()],
  props: {
    isSubmitting: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      // Put a list of inputs or computed properties here to be sent for marking
      inputs: {
        massKHP1: null,
        volInit1: null,
        volFinal1: null,
        molKHP1: null,
        molNaOH1: null,
        volNaOH1: null,
        concNaOH1: null,

        massKHP2: null,
        volInit2: null,
        volFinal2: null,
        molKHP2: null,
        molNaOH2: null,
        volNaOH2: null,
        concNaOH2: null,

        concNaOHAvg: null,

        volInitB1: null,
        volFinalB1: null,
        volNaOHB1: null,
        molNaOHB1: null,
        molHClB1: null,
        concHClB1: null,

        volInitB2: null,
        volFinalB2: null,
        volNaOHB2: null,
        molNaOHB2: null,
        molHClB2: null,
        concHClB2: null,

        concHClAvg: null,
      },
      labResults1: [
        {
          property: '1. Mass of KHP (g)',
          value1: 'massKHP1',
          value2: 'massKHP2',
        },
        {
          property: '2. Initial burette volume reading (mL NaOH)',
          value1: 'volInit1',
          value2: 'volInit2',
        },
        {
          property: '3. Final burette volume reading (mL NaOH)',
          value1: 'volFinal1',
          value2: 'volFinal2',
        },
        {
          property: '4. Moles of KHP used (mol)',
          value1: 'molKHP1',
          value2: 'molKHP2',
        },
        {
          property: '5. Moles of NaOH (mol)',
          value1: 'molNaOH1',
          value2: 'molNaOH2',
        },
        {
          property: '6. Volume of NaOH used (mL)',
          value1: 'volNaOH1',
          value2: 'volNaOH2',
        },
        {
          property: '7. Molarity of NaOH (M)',
          value1: 'concNaOH1',
          value2: 'concNaOH2',
        },
      ],
      labResults2A: [
        {
          property: '1. Initial burette volume reading (mL NaOH)',
          value1: 'volInitB1',
          value2: 'volInitB2',
        },
        {
          property: '2. Final burette volume reading (mL NaOH)',
          value1: 'volFinalB1',
          value2: 'volFinalB2',
        },
        {
          property: '3. Volume of NaOH used (mL)',
          value1: 'volNaOHB1',
          value2: 'volNaOHB2',
        },
        {
          property: '4. Concentration of NaOH (M)$^\\text{*}$',
          value1: 'concNaOHAvg',
          value2: 'concNaOHAvg',
        },
        {
          property: '5. Moles of NaOH (mol)',
          value1: 'molNaOHB1',
          value2: 'molNaOHB2',
        },
        {
          property: '6. Moles of HCl (mol)',
          value1: 'molHClB1',
          value2: 'molHClB2',
          value3: 'molHClB3',
        },
      ],
      labResults2B: [
        {
          property: '8. Molarity of HCl (M)',
          value1: 'concHClB1',
          value2: 'concHClB2',
          value3: 'concHClB3',
        },
      ],
    };
  },
};
</script>

<style></style>
